/**
 * 助手消息的「过程轨迹」：深度思考 + 工具调用。
 *
 * 这些内容不与正文混排——正文区只留最终回答，过程收进气泡上方的折叠区
 * （默认折叠）。流式过程中由 SSE 事件累积，历史消息则由 TOOL 消息回放拼装。
 */
import { AiMessage } from '../../api/aiAssistant';

export interface ToolTraceItem {
  /** 后端的 tool_call id；历史回放时用它把 TOOL 结果挂回发起它的 assistant 消息 */
  toolCallId?: string;
  name: string;
  /** 模型给出的入参 JSON 字符串 */
  arguments?: string;
  /** 工具返回结果（历史是截断摘要，流式是 200 字摘要）*/
  result?: string;
  /** 还在执行中（流式过程里 tool_call 已发、tool_result 未到） */
  running?: boolean;
}

interface RawToolCall {
  id?: string;
  name?: string;
  arguments?: string;
}

/** 解析 assistant 消息的 tool_calls JSON，坏数据当作没有 */
export function parseToolCalls(json?: string | null): ToolTraceItem[] {
  if (!json) {
    return [];
  }
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return (parsed as RawToolCall[])
      .filter((item) => item && item.name)
      .map((item) => ({
        toolCallId: item.id,
        name: item.name as string,
        arguments: item.arguments,
      }));
  } catch {
    return [];
  }
}

/**
 * 一批工具调用 = agent loop 的一轮。模型一轮里可能并发调好几个工具，
 * 这些属于同一次「决策」，展示时要 group 在一起，不能和下一轮混作一堆。
 */
export type ToolBatch = ToolTraceItem[];

/** skill_load 下发的单个已加载技能（prompt 全文属于管理端配置，服务端不外发） */
export interface SkillTraceSkill {
  name: string;
  description?: string;
}

/**
 * 技能装配过程：skill_search（配置里筛启用可用的）→ skill_load（注入 system prompt）。
 * 服务端不落库，只在流式过程中下发，历史回放不带。
 */
export interface SkillTrace {
  /** 配置的技能总数 */
  total: number;
  /** 启用且可用（实际注入 prompt）的技能数 */
  matched: number;
  /** 命中技能名 */
  names: string[];
  /** 已加载技能清单（skill_load 到达后回填；命中 0 个时始终缺省） */
  skills?: SkillTraceSkill[];
}

/**
 * skill_load 回填进已有的 skillTrace。协议上 skill_search 一定先到；
 * 万一缺失（异常顺序/中间层丢事件），用加载清单兜底补齐搜索信息，别让加载结果没处挂。
 */
export function applySkillLoad(
  existing: SkillTrace | undefined,
  skills: SkillTraceSkill[],
): SkillTrace {
  return {
    ...(existing || {
      total: skills.length,
      matched: skills.length,
      names: skills.map((skill) => skill.name),
    }),
    skills,
  };
}

export type TraceMessage = AiMessage & {
  /** 流式过程中累积的思考内容（历史消息用 reasoningContent） */
  liveReasoning?: string;
  /** 按轮次分组的工具调用 */
  toolBatches?: ToolBatch[];
  /** 技能装配过程（仅流式，历史回放无） */
  skillTrace?: SkillTrace;
};

/**
 * 把服务端返回的原始消息列表折叠成气泡列表：
 * - TOOL 消息不单独成泡，按 toolCallId 挂回发起它的 assistant 消息
 * - 既无正文、又无图表、又非失败的 assistant 占位行丢弃，但**发起过工具调用的
 *   要保留**，否则那一轮的工具轨迹就没地方挂
 */
export function foldMessages(messages: AiMessage[]): TraceMessage[] {
  const folded: TraceMessage[] = [];
  const byToolCallId = new Map<string, ToolTraceItem>();

  messages.forEach((message) => {
    if (message.role === 'TOOL') {
      const item = message.toolCallId ? byToolCallId.get(message.toolCallId) : undefined;
      if (item) {
        item.result = message.content;
        return;
      }
      // 找不到配对的 assistant（历史残缺）：挂到最近一条助手消息的最后一批上，不丢内容
      const last = [...folded].reverse().find((item2) => item2.role === 'ASSISTANT');
      if (last) {
        const orphan: ToolTraceItem = {
          toolCallId: message.toolCallId,
          name: message.toolName || '未知工具',
          result: message.content,
        };
        const batches = last.toolBatches || [];
        last.toolBatches = batches.length > 0
          ? [...batches.slice(0, -1), [...batches[batches.length - 1], orphan]]
          : [[orphan]];
      }
      return;
    }

    // 一条 assistant 消息 = agent loop 的一轮 = 一批工具调用
    const batch = parseToolCalls(message.toolCalls);
    batch.forEach((item) => {
      if (item.toolCallId) {
        byToolCallId.set(item.toolCallId, item);
      }
    });

    const hasSubstance = Boolean(
      message.content || message.charts || message.reasoningContent
        || batch.length > 0 || message.status === 'FAILED',
    );
    if (!hasSubstance) {
      return;
    }
    folded.push({ ...message, toolBatches: batch.length > 0 ? [batch] : [] });
  });

  return folded;
}

/** 展平所有批次，用于计数与「有没有工具调用」的判断 */
export function flattenBatches(batches?: ToolBatch[]): ToolTraceItem[] {
  return (batches || []).flat();
}

/** 摘要里最多带几个入参值，再多标题就撑破一行了 */
const ARG_SUMMARY_MAX_ITEMS = 3;
const ARG_SUMMARY_MAX_LENGTH = 40;

/** 单个标量值转成一小段可读文本，对象/数组/长文本这类展开也说不清的返回空 */
function scalarText(value: unknown): string {
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'string') {
    const text = value.trim();
    return text && text.length <= 20 ? text : '';
  }
  return '';
}

/**
 * 入参值转摘要：标量直接转，数组只挑里面的标量元素。
 *
 * 刻意不递归——入参是模型生成的，深嵌套数组一旦出现就是渲染时的栈溢出（这段不在
 * try/catch 里，会把整个会话页面炸掉），而嵌套结构本来也塞不进一行标题。
 */
function summarizeValue(value: unknown): string {
  if (!Array.isArray(value)) {
    return scalarText(value);
  }
  const parts: string[] = [];
  for (const item of value) {
    const text = scalarText(item);
    if (text) {
      parts.push(text);
    }
    if (parts.length >= ARG_SUMMARY_MAX_ITEMS) {
      break;
    }
  }
  return parts.join(' ');
}

/**
 * 把工具入参压成折叠标题上的一行摘要——不展开也能看出「查的是什么」。
 * 只取标量与标量数组，嵌套对象一律略过（塞进标题也读不懂）。
 */
export function summarizeArguments(raw?: string): string {
  if (!raw) {
    return '';
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return '';
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return '';
  }
  const parts: string[] = [];
  Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) => {
    if (parts.length >= ARG_SUMMARY_MAX_ITEMS) {
      return;
    }
    const text = summarizeValue(value);
    if (text) {
      // 带上字段名：光一个「30」看不出是 30 天还是 30 条
      parts.push(`${key}=${text}`);
    }
  });
  const summary = parts.join(' ');
  return summary.length > ARG_SUMMARY_MAX_LENGTH
    ? `${summary.slice(0, ARG_SUMMARY_MAX_LENGTH)}…`
    : summary;
}

/**
 * 剥掉模型写进正文的伪工具调用标记。
 *
 * 模型（实测 DeepSeek）在拿不到 tools 的轮次仍想调用工具时，会把调用写成
 * `<｜｜DSML｜｜tool_calls>…</｜｜DSML｜｜tool_calls>` 这类私有标记（全角竖线）。
 * 这段不会被执行，渲染出来就是一坨乱码。后端落库时也会剥（api#199），
 * 前端这层负责流式过程中已经推送过来的增量与历史脏数据。
 */
export function stripInlineToolMarkup(content?: string | null): string {
  if (!content || !content.includes('DSML')) {
    return content || '';
  }
  return content
    .replace(/<｜+DSML｜+tool_calls>[\s\S]*?<\/｜+DSML｜+tool_calls>/g, '')
    .replace(/<｜+DSML｜+tool_calls>[\s\S]*$/, '')
    .trimEnd();
}
